import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useAuth } from '../contexts/AuthContext';
import { rankedLeaguesApi, RankedLeague as RankedLeagueType } from '../services/api';
import UserDisplay from '../components/user/UserDisplay';
import { StarIcon } from '@heroicons/react/24/outline';

const RANKED_GAMES_THRESHOLD = 3;

const RankedLeague: React.FC = () => {
  useRequireAuth();
  const { user } = useAuth();
  const [league, setLeague] = useState<RankedLeagueType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    fetchLeague();
  }, []);

  const fetchLeague = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await rankedLeaguesApi.getCurrent();
      setLeague(response.data.league);
    } catch (err: any) {
      setError(err.message || 'Failed to load ranked league');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    setJoining(true);
    setError(null);
    try {
      const response = await rankedLeaguesApi.joinLeague();
      setLeague(response.data.league);
    } catch (err: any) {
      setError(err.message || 'Failed to join ranked league');
    } finally {
      setJoining(false);
    }
  };

  const isInLeague = league && user
    ? league.players.some(p => p.player._id === user._id)
    : false;

  const daysRemaining = league
    ? Math.max(0, 90 - Math.floor((Date.now() - new Date(league.startDate).getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  const rankedPlayers = league
    ? league.players
        .filter(p => p.gamesPlayed >= RANKED_GAMES_THRESHOLD)
        .sort((a, b) => b.rankedPoints - a.rankedPoints)
    : [];

  const unrankedPlayers = league
    ? league.players
        .filter(p => p.gamesPlayed < RANKED_GAMES_THRESHOLD)
        .sort((a, b) => b.gamesPlayed - a.gamesPlayed || a.player._id.localeCompare(b.player._id))
    : [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <StarIcon className="h-8 w-8 text-yellow-500" />
          Ranked League
        </h1>
        <div className="flex items-center gap-3">
          <Link
            to="/games?filter=ranked"
            className="btn-secondary"
          >
            View Ranked Games
          </Link>
          {!loading && league && !isInLeague && (
            <button
              onClick={handleJoin}
              disabled={joining}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {joining ? 'Joining...' : 'Join Ranked League'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="card">
          <p className="text-gray-500 text-center py-8">Loading ranked league...</p>
        </div>
      ) : league ? (
        <>
          <div className="card">
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Season started: {new Date(league.startDate).toLocaleDateString()}</span>
              <span>{daysRemaining} days remaining in season</span>
            </div>
            {isInLeague && (
              <p className="mt-2 text-sm text-green-600 font-medium">You are enrolled in this season.</p>
            )}
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Leaderboard</h2>
            {rankedPlayers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No players have qualified yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 text-sm font-medium text-gray-700 w-12">Rank</th>
                      <th className="text-left py-2 pr-4 text-sm font-medium text-gray-700">Player</th>
                      <th className="text-right py-2 pr-4 text-sm font-medium text-gray-700 w-16">Games</th>
                      <th className="text-right py-2 text-sm font-medium text-gray-700">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankedPlayers.map((entry, index) => (
                      <tr key={entry.player._id} className="border-b border-gray-100 last:border-0">
                        <td className="py-3 pr-4 text-sm font-medium text-gray-500">#{index + 1}</td>
                        <td className="py-3 pr-4">
                          <UserDisplay user={entry.player} size="sm" />
                        </td>
                        <td className="py-3 pr-4 text-right text-sm text-gray-700">{entry.gamesPlayed}</td>
                        <td className="py-3 text-right text-sm font-semibold text-gray-900">
                          {entry.rankedPoints}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {unrankedPlayers.length > 0 && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Unranked</h2>
              <p className="text-sm text-gray-500 mb-4">Less than {RANKED_GAMES_THRESHOLD} games — not yet eligible for the leaderboard</p>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 text-sm font-medium text-gray-700">Player</th>
                      <th className="text-right py-2 text-sm font-medium text-gray-700 w-16">Games</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unrankedPlayers.map((entry) => (
                      <tr key={entry.player._id} className="border-b border-gray-100 last:border-0">
                        <td className="py-3 pr-4">
                          <UserDisplay user={entry.player} size="sm" />
                        </td>
                        <td className="py-3 text-right text-sm text-gray-700">{entry.gamesPlayed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default RankedLeague;
